import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const ForgotPassword = () => {
  const [isOtpSent, setIsOtpSent] = useState(false);
  const navigate = useNavigate();
  const { forgotPassword, resetPassword } = useAuth();

  const getForgotPasswordSchema = (isOtpSent) => 
    Yup.object().shape({
      email: Yup.string()
        .email('Invalid email address')
        .required('Email is required'),
      otp: isOtpSent ? Yup.string().required('OTP is required') : Yup.string(),
      newPassword: isOtpSent ? Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .required('New password is required') : Yup.string(),
      confirmPassword: isOtpSent ? Yup.string()
        .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
        .required('Confirm password is required') : Yup.string()
    });

  const formik = useFormik({
    initialValues: {
      email: '',
      otp: '',
      newPassword: '',
      confirmPassword: '',
      isOtpSent: false
    },
    validationSchema: getForgotPasswordSchema(isOtpSent),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!isOtpSent) {
          await forgotPassword(values.email);
          setIsOtpSent(true);
          toast.success('OTP sent to your email');
        } else {
          await resetPassword(values.email, values.otp, values.newPassword);
          toast.success('Password reset successfully');
          navigate('/login');
        }
      } catch (error) {
        toast.error(error.message || (isOtpSent ? 'Failed to reset password' : 'Failed to send OTP'));
      } finally {
        setSubmitting(false);
      }
    }
  });

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Reset your password
          </h2>
        </div>
        <form onSubmit={formik.handleSubmit} className="mt-8 space-y-6">
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.email}
                disabled={isOtpSent}
                className={`appearance-none rounded-none relative block w-full px-3 py-4 border-2 ${
                  formik.errors.email && formik.touched.email ? 'border-red-500' : 'border-gray-300'
                } placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm`}
                placeholder="Email address"
              />
              {formik.errors.email && formik.touched.email && (
                <div className="text-red-500 text-xs mt-1">{formik.errors.email}</div>
              )}
            </div>
            {isOtpSent && (
              <>
                <div>
                  <label htmlFor="otp" className="sr-only">
                    OTP
                  </label>
                  <input
                    id="otp"
                    name="otp"
                    type="text"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.otp}
                    className={`appearance-none rounded-none relative block w-full px-3 py-4 border-2 ${
                      formik.errors.otp && formik.touched.otp ? 'border-red-500' : 'border-gray-300'
                    } placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm`}
                    placeholder="Enter OTP"
                  />
                  {formik.errors.otp && formik.touched.otp && (
                    <div className="text-red-500 text-xs mt-1">{formik.errors.otp}</div>
                  )}
                </div>
                <div>
                  <label htmlFor="newPassword" className="sr-only">
                    New Password
                  </label>
                  <input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    autoComplete="new-password"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.newPassword}
                    className={`appearance-none rounded-none relative block w-full px-3 py-4 border-2 ${
                      formik.errors.newPassword && formik.touched.newPassword ? 'border-red-500' : 'border-gray-300'
                    } placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm`}
                    placeholder="New Password"
                  />
                  {formik.errors.newPassword && formik.touched.newPassword && (
                    <div className="text-red-500 text-xs mt-1">{formik.errors.newPassword}</div>
                  )}
                </div>
                <div>
                  <label htmlFor="confirmPassword" className="sr-only">
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.confirmPassword}
                    className={`appearance-none rounded-none relative block w-full px-3 py-4 border-2 ${
                      formik.errors.confirmPassword && formik.touched.confirmPassword ? 'border-red-500' : 'border-gray-300'
                    } placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm`}
                    placeholder="Confirm Password"
                  />
                  {formik.errors.confirmPassword && formik.touched.confirmPassword && (
                    <div className="text-red-500 text-xs mt-1">{formik.errors.confirmPassword}</div>
                  )}
                </div>
              </>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={formik.isSubmitting}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              {formik.isSubmitting ? (
                <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </span>
              ) : null}
              {isOtpSent ? 'Reset Password' : 'Send OTP'}
            </button>
          </div>

          <div className="text-center">
            <Link to="/login" className="font-medium text-primary hover:text-primary-dark">
              Back to login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword; 